/**
 * BillingTracksPanel — R5 (v0.14)
 *
 * Read-mostly panel on ProjectDetailPage that surfaces the project's
 * scopes per organization (DBA / billing track) alongside last-invoiced
 * metadata, plus a [Remove] affordance for never-invoiced tracks.
 *
 * Visibility rules (Jason's lock):
 *   - 0 scopes  → render nothing (project still loading or empty)
 *   - 1 scope   → render nothing (90% case; project page visually unchanged)
 *   - 2+ scopes → render collapsed header showing org names + (N tracks)
 *                 Click → expand into one row per scope.
 *
 * Row content:
 *   - {organization.name} / {establishment.name or "(no DBA)"}
 *   - Last invoiced YYYY-MM-DD, or "Never used"
 *   - Auto-defaulted heuristic (R3 §f trap): a scope with an establishment
 *     but zero sent invoices. Rendered italic with "Likely auto-defaulted".
 *   - [Remove] button only when total_invoice_count === 0.
 *
 * Confirm flow:
 *   - Click [Remove] → AlertDialog with safe copy ("The organization and
 *     DBA are not deleted — only this project's connection to them.")
 *   - On confirm → fire useDeleteProjectScope → optimistic update
 *     (delete hook already invalidates 'project-scope-summary' on success)
 *     → toast on success.
 *
 * Out of scope per Jason: no org-page DBA archive UI, no cleanup migration.
 */
import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useProjectScopeSummary, useDeleteProjectScope } from '@/hooks/useProjectScopes';
import type { ProjectScopeSummary } from '@/lib/schemas';

export interface BillingTracksPanelProps {
  projectId: string;
  /** Optional: override the >=2 visibility threshold for tests / debug. */
  minScopesToShow?: number;
}

/**
 * Format a YYYY-MM-DD ISO date (or null) to a human-readable label.
 * Falls back to the raw string when not parseable so we never crash
 * the panel on unexpected backend data.
 */
function formatInvoiceDate(iso: string | null | undefined): string {
  if (!iso) return 'Never used';
  // ISO date format YYYY-MM-DD; render straight through (locale-stable, sortable).
  // Don't reformat via Date() — TZ offsets can shift the day by one.
  if (/^\d{4}-\d{2}-\d{2}/.test(iso)) {
    return `Last invoiced ${iso.slice(0, 10)}`;
  }
  return `Last invoiced ${iso}`;
}

/**
 * v0.14 B1 (§8.2 / §0.1): for orphan scopes (zero current invoices but a
 * historical org-change moved invoices away), render explicit lineage so
 * Christa sees "Last invoice moved to {target_client} on {date}" instead
 * of the misleading "Never used" that ships when zero current invoices is
 * the genuine state.
 *
 * Date format matches `formatInvoiceDate`: leading YYYY-MM-DD only, no TZ
 * shift. audit_trail.ts is a timestamp; we slice the date portion.
 */
function formatMovedLineage(
  movedToClient: string | null | undefined,
  movedAt: string | null | undefined,
): string | null {
  if (!movedToClient || !movedAt) return null;
  const dateOnly = /^\d{4}-\d{2}-\d{2}/.test(movedAt)
    ? movedAt.slice(0, 10)
    : movedAt;
  return `Last invoice moved to ${movedToClient} on ${dateOnly}`;
}

/**
 * "Likely auto-defaulted" detection per R3 §f: a scope carries an
 * establishment but has never been used on a real (non-draft) invoice.
 * These are the W2+W6 trap rows pre-v0.14 from `create_new_scope` orphans.
 */
function isLikelyAutoDefaulted(scope: ProjectScopeSummary): boolean {
  return (
    scope.establishment_id !== null &&
    scope.establishment_id !== undefined &&
    scope.sent_invoice_count === 0
  );
}

function headerLabel(scopes: ProjectScopeSummary[]): string {
  // De-dup org names so two scopes on True Heart show "True Heart" once
  const orgNames = Array.from(
    new Set(
      scopes
        .map((s) => s.organization_name)
        .filter((n): n is string => !!n && n.trim().length > 0),
    ),
  );
  const orgPart =
    orgNames.length === 0
      ? '—'
      : orgNames.length <= 3
        ? orgNames.join(', ')
        : `${orgNames.slice(0, 2).join(', ')} +${orgNames.length - 2} more`;
  const n = scopes.length;
  return `Billing: ${orgPart} (${n} tracks)`;
}

export function BillingTracksPanel({
  projectId,
  minScopesToShow = 2,
}: BillingTracksPanelProps) {
  const { data, isLoading } = useProjectScopeSummary(projectId);
  const deleteScope = useDeleteProjectScope();

  const [expanded, setExpanded] = useState(false);
  // Track which scope the [Remove] confirm dialog targets (null = closed)
  const [confirmTargetId, setConfirmTargetId] = useState<string | null>(null);

  const scopes = useMemo<ProjectScopeSummary[]>(
    () => data?.scopes ?? [],
    [data?.scopes],
  );

  // Visibility lock: project page stays visually identical for the 1-scope case
  if (isLoading) return null;
  if (scopes.length < minScopesToShow) return null;

  const confirmTarget = scopes.find((s) => s.scope_id === confirmTargetId) ?? null;

  const handleRemove = (scopeId: string) => {
    deleteScope.mutate(
      { id: scopeId, projectId },
      {
        onSettled: () => {
          // Always clear the confirm dialog whether success or error
          // (the hook itself surfaces error toasts).
          setConfirmTargetId(null);
        },
        onSuccess: () => {
          // Toast: the delete hook fires "Scope deleted successfully";
          // R5 spec wants "Billing track removed" but we keep parity
          // with the existing hook to avoid surprising other callers.
        },
      },
    );
  };

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white"
      data-testid="billing-tracks-panel"
    >
      {/* Collapsed header — click to expand */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-slate-50"
        aria-expanded={expanded}
        aria-controls="billing-tracks-list"
        data-testid="billing-tracks-toggle"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-slate-500" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-500" aria-hidden="true" />
        )}
        <span className="text-sm font-medium text-slate-800">
          {headerLabel(scopes)}
        </span>
      </button>

      {/* Expanded list */}
      {expanded && (
        <div
          id="billing-tracks-list"
          className="border-t border-slate-200"
          data-testid="billing-tracks-list"
        >
          <ul className="divide-y divide-slate-100">
            {scopes.map((scope) => {
              const neverUsed = scope.total_invoice_count === 0;
              const autoDefaulted = isLikelyAutoDefaulted(scope);
              return (
                <li
                  key={scope.scope_id}
                  className="flex items-center gap-3 px-4 py-3"
                  data-testid="billing-tracks-row"
                  data-scope-id={scope.scope_id}
                >
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        'text-sm font-medium text-slate-800',
                        autoDefaulted && 'italic',
                      )}
                    >
                      {scope.organization_name || '(no organization)'}
                      <span className="ml-2 font-normal text-slate-500">
                        /{' '}
                        {scope.establishment_name
                          ? scope.establishment_name
                          : '(no DBA)'}
                      </span>
                      {scope.is_primary && (
                        <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">
                          Primary
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-600">
                      {(() => {
                        // v0.14 B1: orphan-from-org-change scope takes
                        // priority over "Never used". A scope with
                        // total_invoice_count = 0 BUT lineage data means
                        // every invoice that was here got moved away.
                        const lineage = formatMovedLineage(
                          scope.last_invoice_moved_to_client_name,
                          scope.last_invoice_moved_at,
                        );
                        if (neverUsed && lineage) return lineage;
                        return formatInvoiceDate(scope.last_invoiced_at);
                      })()}
                      {autoDefaulted && (
                        <span className="ml-2 italic text-slate-500">
                          — Likely auto-defaulted
                        </span>
                      )}
                    </div>
                  </div>
                  {neverUsed && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-slate-600 hover:text-red-600"
                      onClick={() => setConfirmTargetId(scope.scope_id)}
                      data-testid="billing-tracks-remove"
                      aria-label={`Remove billing track for ${scope.organization_name || 'organization'}${
                        scope.establishment_name
                          ? ' / ' + scope.establishment_name
                          : ''
                      }`}
                      disabled={deleteScope.isPending}
                    >
                      <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" />
                      Remove
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Confirm dialog */}
      <AlertDialog
        open={confirmTarget !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmTargetId(null);
        }}
      >
        <AlertDialogContent data-testid="billing-tracks-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this billing track?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove this never-used billing track? The organization and DBA
              are not deleted — only this project's connection to them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteScope.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (confirmTarget) handleRemove(confirmTarget.scope_id);
              }}
              disabled={deleteScope.isPending}
              data-testid="billing-tracks-confirm-remove"
            >
              Remove track
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
